import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Download, Calendar, 
  Search, Filter, FileSpreadsheet, 
  CheckCircle2, Clock, Lock, Sparkles,
  FileText,
  AlertCircle, Loader2, RefreshCw, X
} from 'lucide-react';
import Avatar from '../../../components/Avatar/Avatar';
import { attendanceService } from '../../../services/api';
import './AttendanceReportsPage.css';

interface MonthlyReport {
  id: number;
  period: string;
  year: number;
  month: number;
  status: 'closed' | 'open' | 'review';
  employeesCount: number;
  totalHours: number;
  totalMinutes: number;
  workDays: number;
  inconsistencies: number;
  generatedDate: string;
  sentToAccounting: boolean;
  sentAt?: string;
}

interface EmployeeReport {
  id: number;
  userId: number;
  userName: string;
  userEmail: string;
  userAvatar?: string;
  totalHours: number;
  totalMinutes: number;
  workDays: number;
  absences: number;
  overtimeHours: number;
  status: 'ok' | 'warning' | 'error';
}

interface MedicalCertificate {
  id?: number;
  userId: number;
  date: string;
  startTime: string;
  endTime: string;
  description?: string;
}

interface AbsenceDay {
  date: string;
  dateFormatted: string;
  weekday: string;
  hasCertificate: boolean;
  certificates: MedicalCertificate[];
}

const AttendanceReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState<MonthlyReport[]>([]);
  const [selectedReport, setSelectedReport] = useState<MonthlyReport | null>(null);
  const [employees, setEmployees] = useState<EmployeeReport[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncYear, setSyncYear] = useState(new Date().getFullYear());
  const [syncMonth, setSyncMonth] = useState(new Date().getMonth() + 1);
  const [availablePeriods, setAvailablePeriods] = useState<Array<{year: number, month: number, users_count: number, logs_count: number}>>([]);
  const [loadingPeriods, setLoadingPeriods] = useState(false);
  const [showAbsenceModal, setShowAbsenceModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeReport | null>(null);
  const [absenceDays, setAbsenceDays] = useState<AbsenceDay[]>([]);
  const [certificates, setCertificates] = useState<MedicalCertificate[]>([]);
  const [loadingCertificates, setLoadingCertificates] = useState(false);
  const [editingCertificate, setEditingCertificate] = useState<MedicalCertificate | null>(null);
  
  // Ref para evitar loop infinito no loadEmployees
  const lastLoadedPeriodRef = useRef<{year: number, month: number} | null>(null);

  // Funções auxiliares
  const getMonthName = (month: number): string => {
    const months = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    return months[month - 1] || '';
  };

  const calculateAbsences = (workDays: number, month: number, year: number): number => {
    // Calcular dias úteis esperados até a data atual (excluindo domingos e dias futuros)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const lastDay = new Date(year, month, 0).getDate();
    let expectedWorkDays = 0;
    
    for (let day = 1; day <= lastDay; day++) {
      const date = new Date(year, month - 1, day);
      date.setHours(0, 0, 0, 0);
      
      // Pular dias futuros
      if (date > today) {
        continue;
      }
      
      const weekday = date.getDay();
      // Contar apenas dias úteis (segunda a sábado, excluindo domingos)
      if (weekday !== 0) { // 0 = domingo
        expectedWorkDays++;
      }
    }
    
    return Math.max(0, expectedWorkDays - workDays);
  };

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedReport && selectedReport.year && selectedReport.month) {
      const periodKey = `${selectedReport.year}-${selectedReport.month}`;
      const lastPeriod = lastLoadedPeriodRef.current;
      const lastPeriodKey = lastPeriod ? `${lastPeriod.year}-${lastPeriod.month}` : null;
      
      // Só carregar se for um período diferente do último carregado
      if (periodKey !== lastPeriodKey) {
        lastLoadedPeriodRef.current = { year: selectedReport.year, month: selectedReport.month };
        loadEmployees(selectedReport.year, selectedReport.month);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReport?.year, selectedReport?.month]);

  const loadReports = async () => {
    try {
      setLoading(true);
      setError(null);

      const currentYear = new Date().getFullYear();
      
      // Tentar buscar do ano atual primeiro
      let response = await attendanceService.getReports({
        year: currentYear
      });
      
      // Se não encontrar nada no ano atual, tentar o ano anterior
      if (response.success && (!response.data || response.data.length === 0)) {
        response = await attendanceService.getReports({
          year: currentYear - 1
        });
      }
      
      // Se ainda não encontrar, buscar todos (sem filtro de ano)
      if (response.success && (!response.data || response.data.length === 0)) {
        response = await attendanceService.getReports({});
      }

      if (response.success && response.data) {
        const formattedReports = response.data.map((report: any) => ({
          id: report.id,
          period: `${getMonthName(report.month)} / ${report.year}`,
          year: report.year,
          month: report.month,
          status: report.sent_to_accounting ? 'closed' : 'review' as 'closed' | 'review',
          employeesCount: 0, // Será calculado no groupReportsByPeriod
          totalHours: parseFloat(report.total_hours?.toString() || '0'),
          totalMinutes: report.total_minutes || 0,
          workDays: report.work_days || 0,
          inconsistencies: 0, // Será calculado
          generatedDate: report.created_at ? new Date(report.created_at).toLocaleDateString('pt-BR') : '',
          sentToAccounting: report.sent_to_accounting || false,
          sentAt: report.sent_at ? new Date(report.sent_at).toLocaleDateString('pt-BR') : undefined,
          user_id: report.user_id // Incluir user_id para contagem correta
        }));

        // Agrupar por período e calcular estatísticas
        const groupedReports = groupReportsByPeriod(formattedReports);
        setReports(groupedReports);

        // Selecionar o primeiro mês se disponível
        if (groupedReports.length > 0 && !selectedReport) {
          setSelectedReport(groupedReports[0]);
        }
      }
    } catch (err: any) {
      console.error('Erro ao carregar relatórios:', err);
      setError(err.response?.data?.message || 'Erro ao carregar relatórios');
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async (year: number, month: number) => {
    try {
      setLoadingEmployees(true);
      setError(null);

      // Buscar todos os relatórios do período
      const response = await attendanceService.getReports({
        year,
        month,
        sentToAccounting: false // Buscar todos, não apenas enviados
      });

      if (response.success && response.data) {
        const employeeReports: EmployeeReport[] = response.data.map((report: any) => ({
          id: report.id,
          userId: report.user_id,
          userName: report.user_name || 'Usuário',
          userEmail: report.user_email || '',
          userAvatar: report.user_avatar_url || undefined,
          totalHours: parseFloat(report.total_hours?.toString() || '0'),
          totalMinutes: report.total_minutes || 0,
          workDays: report.work_days || 0,
          absences: calculateAbsences(report.work_days, month, year),
          overtimeHours: 0, // Será calculado
          status: report.work_days >= 20 ? 'ok' : report.work_days >= 15 ? 'warning' : 'error' as 'ok' | 'warning' | 'error'
        }));

        setEmployees(employeeReports);

        // Atualizar relatório selecionado com contagem de colaboradores
        // Usar callback para evitar loop infinito - só atualizar se os valores mudaram
        setSelectedReport(prev => {
          if (prev && prev.year === year && prev.month === month) {
            const newEmployeesCount = employeeReports.length;
            const newInconsistencies = employeeReports.filter(e => e.status !== 'ok').length;
            
            // Só atualizar se os valores realmente mudaram
            if (prev.employeesCount !== newEmployeesCount || 
                prev.inconsistencies !== newInconsistencies) {
              return {
                ...prev,
                employeesCount: newEmployeesCount,
                inconsistencies: newInconsistencies
              };
            }
          }
          return prev;
        });
        
        // Atualizar também na lista de relatórios
        setReports(prev => prev.map(r => {
          if (r.year === year && r.month === month) {
            return {
              ...r,
              employeesCount: employeeReports.length,
              inconsistencies: employeeReports.filter(e => e.status !== 'ok').length
            };
          }
          return r;
        }));
      }
    } catch (err: any) {
      console.error('Erro ao carregar colaboradores:', err);
      setError(err.response?.data?.message || 'Erro ao carregar colaboradores');
    } finally {
      setLoadingEmployees(false);
    }
  };

  const groupReportsByPeriod = (reports: MonthlyReport[]): MonthlyReport[] => {
    // Agrupar por período e calcular estatísticas
    const grouped = new Map<string, {
      report: MonthlyReport;
      userIds: Set<number>;
      totalHours: number;
      totalMinutes: number;
    }>();

    reports.forEach((report) => {
      const key = `${report.year}-${report.month}`;
      const userId = (report as any).user_id;
      
      if (!grouped.has(key)) {
        const userIds = new Set<number>();
        if (userId) {
          userIds.add(userId);
        }
        grouped.set(key, {
          report: {
            ...report,
            employeesCount: userIds.size
          },
          userIds,
          totalHours: report.totalHours || 0,
          totalMinutes: report.totalMinutes || 0
        });
      } else {
        // Se já existe, adicionar user_id único e somar horas
        const existing = grouped.get(key)!;
        if (userId) {
          existing.userIds.add(userId);
        }
        existing.totalHours += report.totalHours || 0;
        existing.totalMinutes += report.totalMinutes || 0;
        // Atualizar o report com os valores corretos
        existing.report.employeesCount = existing.userIds.size;
        existing.report.totalHours = existing.totalHours;
        existing.report.totalMinutes = existing.totalMinutes;
      }
    });

    return Array.from(grouped.values()).map(g => g.report).sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.month - a.month;
    });
  };

  const formatHours = (hours: number): string => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m.toString().padStart(2, '0')}m`;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'closed':
        return (
          <span className="status-badge status-closed">
            <Lock size={10} />
            Fechado
          </span>
        );
      case 'review':
        return (
          <span className="status-badge status-review">
            <Clock size={10} />
            Em Revisão
          </span>
        );
      default:
        return (
          <span className="status-badge status-open">
            Aberto
          </span>
        );
    }
  };

  const handleReportClick = (report: MonthlyReport) => {
    setSelectedReport(report);
  };

  const handleViewBook = (userId?: number) => {
    if (!selectedReport) return;
    
    const params = new URLSearchParams({
      month: selectedReport.month.toString(),
      year: selectedReport.year.toString()
    });
    
    if (userId) {
      params.set('userId', userId.toString());
    }
    
    navigate(`/erp/documents/attendance-log?${params.toString()}`);
  };

  const handleSendReport = async (reportId: number) => {
    try {
      setLoading(true);
      const response = await attendanceService.sendReport(reportId);

      if (response.success) {
        await loadReports();
        if (selectedReport && selectedReport.id === reportId) {
          setSelectedReport({ ...selectedReport, sentToAccounting: true, status: 'closed' });
        }
      }
    } catch (err: any) {
      console.error('Erro ao enviar relatório:', err);
      setError(err.response?.data?.message || 'Erro ao enviar relatório para contabilidade');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSyncModal = async () => {
    const now = new Date();
    setSyncYear(now.getFullYear());
    setSyncMonth(now.getMonth() + 1);
    setShowSyncModal(true);
    
    // Carregar períodos disponíveis
    try {
      setLoadingPeriods(true);
      const response = await attendanceService.getAvailablePeriods();
      if (response.success && response.data) {
        setAvailablePeriods(response.data);
      }
    } catch (err) {
      console.error('Erro ao carregar períodos disponíveis:', err);
    } finally {
      setLoadingPeriods(false);
    }
  };

  const handleSyncReports = async () => {
    try {
      setSyncing(true);
      setError(null);
      setShowSyncModal(false);

      const response = await attendanceService.generateReport({
        year: syncYear,
        month: syncMonth
      });

      if (response.success) {
        // Recarregar os relatórios após a sincronização
        await loadReports();
        setError(null);
      } else {
        setError(response.message || 'Erro ao sincronizar relatórios');
      }
    } catch (err: any) {
      console.error('Erro ao sincronizar relatórios:', err);
      setError(err.response?.data?.message || 'Erro ao sincronizar relatórios de ponto');
    } finally {
      setSyncing(false);
    }
  };

  const handleOpenAbsenceModal = async (employee: EmployeeReport) => {
    if (!selectedReport) return;
    
    setSelectedEmployee(employee);
    setShowAbsenceModal(true);
    setEditingCertificate(null);
    
    // Calcular dias de falta
    await loadAbsenceDays(employee.userId, selectedReport.year, selectedReport.month);
  };

  const loadAbsenceDays = async (userId: number, year: number, month: number) => {
    try {
      setLoadingCertificates(true);
      
      // Calcular período
      const dateFrom = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const dateTo = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
      
      // Buscar atestados
      const certResponse = await attendanceService.getMedicalCertificates({
        userId,
        dateFrom,
        dateTo
      });
      
      const certs = certResponse.success ? certResponse.data : [];
      setCertificates(certs);
      
      // Buscar logs do período
      const logsResponse = await attendanceService.getLogs({
        userId,
        dateFrom,
        dateTo
      });
      
      const logs = logsResponse.success ? logsResponse.data : [];
      
      // Função auxiliar para normalizar data
      const normalizeDate = (dateValue: any): string | null => {
        if (!dateValue) return null;
        
        let dateStr: string;
        
        // Se for objeto Date, converter para string
        if (dateValue instanceof Date) {
          dateStr = dateValue.toISOString().split('T')[0];
        } else if (typeof dateValue === 'string') {
          dateStr = dateValue;
        } else {
          // Tentar converter para string
          dateStr = String(dateValue);
        }
        
        // Se a data vier como string ISO com timestamp, extrair apenas a data
        if (dateStr.includes('T')) {
          dateStr = dateStr.split('T')[0];
        }
        
        // Garantir formato YYYY-MM-DD
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return dateStr;
        }
        
        // Tentar converter de outros formatos (DD/MM/YYYY, etc)
        const dateObj = new Date(dateStr);
        if (!isNaN(dateObj.getTime())) {
          const year = dateObj.getFullYear();
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          const day = String(dateObj.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
        
        return null;
      };
      
      // Agrupar logs por data e verificar se tem pelo menos um par entrada/saída
      const datesWithWork = new Map<string, { hasIn: boolean, hasOut: boolean }>();
      
      logs.forEach((log: any) => {
        const dateStr = normalizeDate(log.date);
        
        if (!dateStr) return;
        
        if (!datesWithWork.has(dateStr)) {
          datesWithWork.set(dateStr, { hasIn: false, hasOut: false });
        }
        const dayLogs = datesWithWork.get(dateStr)!;
        
        if (log.type === 'in') {
          dayLogs.hasIn = true;
        } else if (log.type === 'out') {
          dayLogs.hasOut = true;
        }
      });
      
      // Calcular dias úteis esperados até hoje
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const absenceDaysList: AbsenceDay[] = [];
      
      for (let day = 1; day <= lastDay; day++) {
        const date = new Date(year, month - 1, day);
        date.setHours(0, 0, 0, 0);
        
        // Pular dias futuros
        if (date > today) continue;
        
        const weekday = date.getDay();
        // Pular domingos (DSR)
        if (weekday === 0) continue;
        
        const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        
        // Verificar se o dia tem trabalho (pelo menos uma entrada E uma saída)
        // Se não tiver entrada E saída, é falta (mesmo que tenha apenas entrada ou apenas saída)
        const dayWork = datesWithWork.get(dateStr);
        const hasWorked = dayWork && dayWork.hasIn && dayWork.hasOut;
        
        // Se não trabalhou (não tem entrada E saída), é falta
        if (!hasWorked) {
          const dayCerts = certs.filter((c: MedicalCertificate) => c.date === dateStr);
          
          absenceDaysList.push({
            date: dateStr,
            dateFormatted: `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`,
            weekday: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'][weekday],
            hasCertificate: dayCerts.length > 0,
            certificates: dayCerts
          });
        }
      }
      
      setAbsenceDays(absenceDaysList);
    } catch (err: any) {
      console.error('Erro ao carregar faltas:', err);
      setError(err.response?.data?.message || 'Erro ao carregar faltas');
    } finally {
      setLoadingCertificates(false);
    }
  };

  const handleSaveCertificate = async (certificate: MedicalCertificate) => {
    if (!selectedEmployee || !selectedReport) return;
    
    try {
      setLoadingCertificates(true);
      
      const data = {
        userId: selectedEmployee.userId,
        date: certificate.date,
        startTime: certificate.startTime,
        endTime: certificate.endTime,
        description: certificate.description || '',
        id: certificate.id
      };
      
      const response = await attendanceService.saveMedicalCertificate(data);
      
      if (response.success) {
        // Recarregar faltas e atestados
        await loadAbsenceDays(selectedEmployee.userId, selectedReport.year, selectedReport.month);
        setEditingCertificate(null);
        
        // Recarregar relatórios para atualizar contagem
        await loadReports();
      }
    } catch (err: any) {
      console.error('Erro ao salvar atestado:', err);
      setError(err.response?.data?.message || 'Erro ao salvar atestado');
    } finally {
      setLoadingCertificates(false);
    }
  };

  const handleDeleteCertificate = async (id: number) => {
    if (!selectedEmployee || !selectedReport) return;
    
    try {
      setLoadingCertificates(true);
      
      const response = await attendanceService.deleteMedicalCertificate(id);
      
      if (response.success) {
        // Recarregar faltas e atestados
        await loadAbsenceDays(selectedEmployee.userId, selectedReport.year, selectedReport.month);
        
        // Recarregar relatórios para atualizar contagem
        await loadReports();
      }
    } catch (err: any) {
      console.error('Erro ao remover atestado:', err);
      setError(err.response?.data?.message || 'Erro ao remover atestado');
    } finally {
      setLoadingCertificates(false);
    }
  };

  const filteredEmployees = employees.filter(emp =>
    emp.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.userEmail.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && reports.length === 0) {
    return (
      <div className="attendance-reports-loading">
        <Loader2 className="animate-spin" size={48} />
        <p>Carregando relatórios...</p>
      </div>
    );
  }

  return (
    <div className="attendance-reports-page">
      {error && (
        <div className="attendance-reports-error">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Header */}
      <div className="attendance-reports-header">
        <div>
          <h1 className="attendance-reports-title">
            Portal <span className="attendance-reports-title-accent">Contabilidade</span>
          </h1>
          <p className="attendance-reports-subtitle">Gestão de Folha e Fechamento de Jornada</p>
        </div>
        <div className="attendance-reports-header-actions">
          <button 
            className="attendance-reports-btn attendance-reports-btn-secondary"
            onClick={handleOpenSyncModal}
            disabled={syncing || loading}
            title="Sincronizar dados de ponto"
          >
            {syncing ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <RefreshCw size={16} />
            )}
            {syncing ? 'Sincronizando...' : 'Sincronizar Dados'}
          </button>
          <button className="attendance-reports-btn attendance-reports-btn-secondary">
            <Filter size={16} />
            Parâmetros Folha
          </button>
          <button className="attendance-reports-btn attendance-reports-btn-primary">
            <Download size={16} />
            Exportar Tudo (CNAB)
          </button>
        </div>
      </div>

      <div className="attendance-reports-grid">
        {/* Monthly Timeline */}
        <div className="attendance-reports-timeline">
          <h3 className="attendance-reports-timeline-title">Fechamentos Automáticos</h3>
          <div className="attendance-reports-timeline-list">
            {reports.length === 0 ? (
              <div className="attendance-reports-empty">
                <Calendar size={48} />
                <p>Nenhum relatório disponível</p>
              </div>
            ) : (
              reports.map((report) => (
                <div
                  key={`${report.year}-${report.month}`}
                  className={`attendance-reports-card ${
                    selectedReport?.year === report.year && selectedReport?.month === report.month
                      ? 'attendance-reports-card-selected'
                      : ''
                  }`}
                  onClick={() => handleReportClick(report)}
                >
                  <div className="attendance-reports-card-header">
                    <div className="attendance-reports-card-icon-wrapper">
                      <Calendar size={20} />
                    </div>
                    <p className="attendance-reports-card-period">{report.period}</p>
                    {getStatusBadge(report.status)}
                  </div>
                  <div className="attendance-reports-card-stats">
                    <div>
                      <p className="attendance-reports-card-label">Colaboradores</p>
                      <p className="attendance-reports-card-value">{report.employeesCount || '-'}</p>
                    </div>
                    <div className="attendance-reports-card-stats-right">
                      <p className="attendance-reports-card-label">Horas Totais</p>
                      <p className="attendance-reports-card-value">{formatHours(report.totalHours)}</p>
                    </div>
                  </div>
                  {report.inconsistencies > 0 && (
                    <div className="attendance-reports-card-warning">
                      <AlertCircle size={14} />
                      {report.inconsistencies} Inconsistências Detectadas
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Details Area */}
        <div className="attendance-reports-details">
          {selectedReport ? (
            <div className="attendance-reports-details-card">
              <div className="attendance-reports-details-header">
                <div>
                  <h3 className="attendance-reports-details-title">
                    Competência: {selectedReport.period}
                  </h3>
                  <div className="attendance-reports-details-status">
                    <div className="attendance-reports-status-dot" />
                    <p>
                      {selectedReport.sentToAccounting
                        ? 'Auditoria Contábil Finalizada'
                        : 'Aguardando Auditoria Contábil para Trancamento'}
                    </p>
                  </div>
                </div>
                <div className="attendance-reports-search-wrapper">
                  <Search className="attendance-reports-search-icon" size={16} />
                  <input
                    type="text"
                    placeholder="Buscar colaborador..."
                    className="attendance-reports-search-input"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <div className="attendance-reports-table-wrapper">
                {loadingEmployees ? (
                  <div className="attendance-reports-table-loading">
                    <Loader2 className="animate-spin" size={32} />
                    <p>Carregando colaboradores...</p>
                  </div>
                ) : (
                  <table className="attendance-reports-table">
                    <thead>
                      <tr>
                        <th>Colaborador</th>
                        <th>Departamento</th>
                        <th className="text-center">Horas</th>
                        <th className="text-center">Faltas</th>
                        <th className="text-center">Status Ponto</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEmployees.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="attendance-reports-table-empty">
                            Nenhum colaborador encontrado
                          </td>
                        </tr>
                      ) : (
                        filteredEmployees.map((emp) => (
                          <tr key={emp.id} className="attendance-reports-table-row">
                            <td>
                              <div className="attendance-reports-table-employee">
                                <Avatar
                                  name={emp.userName}
                                  email={emp.userEmail}
                                  imageUrl={emp.userAvatar}
                                  size="sm"
                                />
                                <span className="attendance-reports-table-name">{emp.userName}</span>
                              </div>
                            </td>
                            <td>
                              <span className="attendance-reports-table-dept">-</span>
                            </td>
                            <td className="text-center">
                              <div className="attendance-reports-table-hours">
                                <span>{formatHours(emp.totalHours)}</span>
                                {emp.overtimeHours > 0 && (
                                  <span className="attendance-reports-table-extras">
                                    +{formatHours(emp.overtimeHours)}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="text-center">
                              <span
                                className={`attendance-reports-table-absences ${
                                  emp.absences > 0 ? 'has-absences' : ''
                                }`}
                              >
                                {emp.absences}
                              </span>
                            </td>
                            <td>
                              <div 
                                className="attendance-reports-table-status"
                                onClick={() => handleOpenAbsenceModal(emp)}
                                style={{ cursor: 'pointer' }}
                                title="Clique para gerenciar faltas e atestados"
                              >
                                {emp.status === 'ok' ? (
                                  <CheckCircle2 size={18} className="status-ok" />
                                ) : (
                                  <AlertCircle size={18} className="status-warning" />
                                )}
                              </div>
                            </td>
                            <td className="text-right">
                              <button className="attendance-reports-table-action">
                                <FileSpreadsheet size={18} />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Action Bar */}
              <div className="attendance-reports-action-bar">
                <div className="attendance-reports-action-info">
                  <div className="attendance-reports-action-icon">
                    <Sparkles size={24} />
                  </div>
                  <div>
                    <h4 className="attendance-reports-action-title">Auditado por Sistema</h4>
                    <p className="attendance-reports-action-subtitle">
                      Nenhuma fraude de geolocalização detectada.
                    </p>
                  </div>
                </div>
                <div className="attendance-reports-action-buttons">
                  <button 
                    className="attendance-reports-action-btn attendance-reports-action-btn-secondary"
                    onClick={() => handleViewBook()}
                  >
                    Visualizar Livro Ponto
                  </button>
                  {!selectedReport.sentToAccounting && (
                    <button
                      className="attendance-reports-action-btn attendance-reports-action-btn-primary"
                      onClick={() => handleSendReport(selectedReport.id)}
                      disabled={loading}
                    >
                      Fechar Mês e Bloquear Edições
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="attendance-reports-no-selection">
              <FileText size={48} />
              <p>Selecione um período para visualizar os dados</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal: Selecionar Período para Sincronização */}
      {showSyncModal && (
        <div className="attendance-reports-modal-overlay" onClick={() => setShowSyncModal(false)}>
          <div className="attendance-reports-modal" onClick={(e) => e.stopPropagation()}>
            <div className="attendance-reports-modal-header">
              <h3 className="attendance-reports-modal-title">Sincronizar Relatórios de Ponto</h3>
              <button 
                onClick={() => setShowSyncModal(false)}
                className="attendance-reports-modal-close"
                disabled={syncing}
              >
                <X size={24} />
              </button>
            </div>
            <div className="attendance-reports-modal-body">
              <p className="attendance-reports-modal-description">
                Selecione o mês e ano para gerar os relatórios de ponto. 
                O sistema buscará apenas usuários com grupo de acesso válido e registros de ponto no período.
              </p>
              
              {availablePeriods.length > 0 && (
                <div className="attendance-reports-modal-periods">
                  <label className="attendance-reports-modal-label">Períodos com registros disponíveis:</label>
                  <div className="attendance-reports-modal-periods-list">
                    {loadingPeriods ? (
                      <div className="attendance-reports-modal-periods-loading">
                        <Loader2 size={16} className="animate-spin" />
                        Carregando...
                      </div>
                    ) : (
                      availablePeriods.map((period) => (
                        <button
                          key={`${period.year}-${period.month}`}
                          type="button"
                          className={`attendance-reports-modal-period-item ${
                            syncYear === period.year && syncMonth === period.month ? 'selected' : ''
                          }`}
                          onClick={() => {
                            setSyncYear(period.year);
                            setSyncMonth(period.month);
                          }}
                          disabled={syncing}
                        >
                          <span className="attendance-reports-modal-period-name">
                            {getMonthName(period.month)} / {period.year}
                          </span>
                          <span className="attendance-reports-modal-period-info">
                            {period.logs_count} registro(s) • {period.users_count} usuário(s)
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
              
              <div className="attendance-reports-modal-fields">
                <div className="attendance-reports-modal-field">
                  <label className="attendance-reports-modal-label">Ano</label>
                  <select
                    className="attendance-reports-modal-select"
                    value={syncYear}
                    onChange={(e) => setSyncYear(parseInt(e.target.value))}
                    disabled={syncing}
                  >
                    {Array.from({ length: 5 }, (_, i) => {
                      const year = new Date().getFullYear() - i;
                      return (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="attendance-reports-modal-field">
                  <label className="attendance-reports-modal-label">Mês</label>
                  <select
                    className="attendance-reports-modal-select"
                    value={syncMonth}
                    onChange={(e) => setSyncMonth(parseInt(e.target.value))}
                    disabled={syncing}
                  >
                    {Array.from({ length: 12 }, (_, i) => {
                      const month = i + 1;
                      return (
                        <option key={month} value={month}>
                          {getMonthName(month)}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            </div>
            <div className="attendance-reports-modal-actions">
              <button
                type="button"
                onClick={() => setShowSyncModal(false)}
                className="attendance-reports-modal-btn-cancel"
                disabled={syncing}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSyncReports}
                className="attendance-reports-modal-btn-submit"
                disabled={syncing}
              >
                {syncing ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Sincronizando...
                  </>
                ) : (
                  <>
                    <RefreshCw size={16} />
                    Sincronizar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Gerenciar Faltas e Atestados */}
      {showAbsenceModal && selectedEmployee && selectedReport && (
        <AbsenceModal
          employee={selectedEmployee}
          year={selectedReport.year}
          month={selectedReport.month}
          absenceDays={absenceDays}
          certificates={certificates}
          loading={loadingCertificates}
          editingCertificate={editingCertificate}
          onClose={() => {
            setShowAbsenceModal(false);
            setSelectedEmployee(null);
            setEditingCertificate(null);
          }}
          onSave={handleSaveCertificate}
          onDelete={handleDeleteCertificate}
          onEdit={(cert) => setEditingCertificate(cert)}
        />
      )}
    </div>
  );
};

// Componente Modal de Faltas e Atestados
interface AbsenceModalProps {
  employee: EmployeeReport;
  year: number;
  month: number;
  absenceDays: AbsenceDay[];
  certificates: MedicalCertificate[];
  loading: boolean;
  editingCertificate: MedicalCertificate | null;
  onClose: () => void;
  onSave: (certificate: MedicalCertificate) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onEdit: (cert: MedicalCertificate) => void;
}

const AbsenceModal: React.FC<AbsenceModalProps> = ({
  employee,
  year,
  month,
  absenceDays,
  certificates,
  loading,
  editingCertificate,
  onClose,
  onSave,
  onDelete,
  onEdit
}) => {
  const [formData, setFormData] = useState<MedicalCertificate>({
    userId: employee.userId,
    date: '',
    startTime: '',
    endTime: '',
    description: ''
  });
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const dateDropdownRef = useRef<HTMLDivElement>(null);
  const dateButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (editingCertificate) {
      setFormData(editingCertificate);
    } else {
      setFormData({
        userId: employee.userId,
        date: '',
        startTime: '',
        endTime: '',
        description: ''
      });
    }
  }, [editingCertificate, employee.userId]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dateDropdownRef.current &&
        !dateDropdownRef.current.contains(event.target as Node) &&
        dateButtonRef.current &&
        !dateButtonRef.current.contains(event.target as Node)
      ) {
        setShowDateDropdown(false);
      }
    };

    if (showDateDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDateDropdown]);

  // Posicionar dropdown
  useEffect(() => {
    if (showDateDropdown && dateDropdownRef.current && dateButtonRef.current) {
      const rect = dateButtonRef.current.getBoundingClientRect();
      dateDropdownRef.current.style.top = `${rect.bottom + 8}px`;
      dateDropdownRef.current.style.left = `${rect.left}px`;
    }
  }, [showDateDropdown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(formData);
  };

  const getMonthName = (month: number): string => {
    const months = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    return months[month - 1] || '';
  };

  return (
    <div className="attendance-reports-modal-overlay" onClick={onClose}>
      <div className="attendance-reports-modal" onClick={(e) => e.stopPropagation()}>
        <div className="attendance-reports-modal-header">
          <h3 className="attendance-reports-modal-title">
            Faltas e Atestados - {employee.userName}
          </h3>
          <button 
            onClick={onClose}
            className="attendance-reports-modal-close"
            disabled={loading}
          >
            <X size={24} />
          </button>
        </div>
        
        <div className="attendance-reports-modal-body">
          <p className="attendance-reports-modal-description">
            Período: {getMonthName(month)} / {year}
          </p>

          {/* Formulário de Atestado */}
          <form onSubmit={handleSubmit} className="attendance-reports-certificate-form">
            <div className="attendance-reports-modal-fields">
              <div className="attendance-reports-modal-field">
                <label className="attendance-reports-modal-label">Data da Falta</label>
                <div className="attendance-reports-date-dropdown-wrapper">
                  <button
                    type="button"
                    ref={dateButtonRef}
                    className="attendance-reports-date-dropdown-button"
                    onClick={() => setShowDateDropdown(!showDateDropdown)}
                    disabled={loading || absenceDays.length === 0}
                  >
                    {formData.date 
                      ? absenceDays.find(d => d.date === formData.date)?.dateFormatted + ' - ' + absenceDays.find(d => d.date === formData.date)?.weekday
                      : 'Selecione uma data'}
                    <span className="attendance-reports-date-dropdown-arrow">▼</span>
                  </button>
                  {showDateDropdown && absenceDays.length > 0 && (
                    <div 
                      ref={dateDropdownRef}
                      className="attendance-reports-date-dropdown"
                    >
                      {absenceDays.map(day => (
                        <button
                          key={day.date}
                          type="button"
                          className={`attendance-reports-date-dropdown-item ${
                            formData.date === day.date ? 'selected' : ''
                          }`}
                          onClick={() => {
                            setFormData({ ...formData, date: day.date });
                            setShowDateDropdown(false);
                          }}
                        >
                          <span className="attendance-reports-date-dropdown-date">{day.dateFormatted}</span>
                          <span className="attendance-reports-date-dropdown-weekday">{day.weekday}</span>
                          {day.hasCertificate && (
                            <span className="attendance-reports-date-dropdown-badge">Atestado</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="attendance-reports-modal-field">
                <label className="attendance-reports-modal-label">Horário Início</label>
                <input
                  type="time"
                  className="attendance-reports-modal-select"
                  value={formData.startTime}
                  onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                  required
                  disabled={loading}
                />
              </div>

              <div className="attendance-reports-modal-field">
                <label className="attendance-reports-modal-label">Horário Fim</label>
                <input
                  type="time"
                  className="attendance-reports-modal-select"
                  value={formData.endTime}
                  onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="attendance-reports-modal-field">
              <label className="attendance-reports-modal-label">Descrição (opcional)</label>
              <textarea
                className="attendance-reports-modal-select"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                disabled={loading}
                placeholder="Ex: Atestado médico, consulta, etc."
              />
            </div>

            <div className="attendance-reports-modal-actions">
              {editingCertificate && (
                <button
                  type="button"
                  onClick={() => onEdit(null as any)}
                  className="attendance-reports-modal-btn-cancel"
                  disabled={loading}
                >
                  Cancelar Edição
                </button>
              )}
              <button
                type="submit"
                className="attendance-reports-modal-btn-submit"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Salvando...
                  </>
                ) : (
                  editingCertificate ? 'Atualizar Atestado' : 'Adicionar Atestado'
                )}
              </button>
            </div>
          </form>

          {/* Lista de Faltas */}
          <div className="attendance-reports-absence-list">
            <h4 className="attendance-reports-absence-list-title">Faltas do Período</h4>
            {loading ? (
              <div className="attendance-reports-table-loading">
                <Loader2 className="animate-spin" size={32} />
                <p>Carregando...</p>
              </div>
            ) : absenceDays.length === 0 ? (
              <p className="attendance-reports-empty-text">Nenhuma falta registrada</p>
            ) : (
              <div className="attendance-reports-absence-items">
                {absenceDays.map(day => (
                  <div key={day.date} className="attendance-reports-absence-item">
                    <div className="attendance-reports-absence-item-header">
                      <div>
                        <span className="attendance-reports-absence-date">{day.dateFormatted}</span>
                        <span className="attendance-reports-absence-weekday">{day.weekday}</span>
                      </div>
                      {day.hasCertificate && (
                        <span className="attendance-reports-absence-badge">Com Atestado</span>
                      )}
                    </div>
                    
                    {day.certificates.length > 0 && (
                      <div className="attendance-reports-certificates-list">
                        {day.certificates.map(cert => (
                          <div key={cert.id} className="attendance-reports-certificate-item">
                            <div className="attendance-reports-certificate-time">
                              {cert.startTime.substring(0, 5)} - {cert.endTime.substring(0, 5)}
                            </div>
                            {cert.description && (
                              <div className="attendance-reports-certificate-desc">{cert.description}</div>
                            )}
                            <div className="attendance-reports-certificate-actions">
                              <button
                                onClick={() => onEdit(cert)}
                                className="attendance-reports-certificate-btn-edit"
                                disabled={loading}
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => cert.id && onDelete(cert.id)}
                                className="attendance-reports-certificate-btn-delete"
                                disabled={loading}
                              >
                                Remover
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceReportsPage;
